<template>

    <ThemeChanger :to="Theme.Blue">

        <PageHeader class="mt-32 lg:mt-48" :header="cms.heading" :header-span="[ 2, 5 ]" :sub-header="cms.description"/>

        <OpenSourceSkeleton class="mt-12 md:mt-24" v-if="isLoading"/>

            <div v-else ref="wrapperReference" class="open-source__wrapper">

                <Container fluid class="pb-0 mt-12 md:mt-24">

                    <Grid v-if="featured.length">

                        <Column>

                            <Heading tag="h2">
                                {{ isCNLocale ? '精选' : 'Featured' }}
                            </Heading>

                        </Column>

                    </Grid>

                </Container>

                <div v-if="featured.length">

                    <template v-for="(openSourcePackage, idx) of featured" :key="`${openSourcePackage.title}-${idx}`">

                        <OpenSourcePackage :data="openSourcePackage" featured :class="[ idx > 0 ? 'mt-24' : 'mt-16' ]"/>

                    </template>

                </div>

                <Container fluid>

                    <Grid :columns="6" class="open-source__sticky-wrapper md:mt-36">

                        <Column
                                :span="6"
                                class="open-source__sticky"
                                :style="offsetStyle"
                                :class="{ '--stickied': isSticky }">

                            <div v-if="categories.length" class="space-x-5 sm:flex sm:flex-nowrap">

                                <button
                                    v-for="item of categories" :key="item.slug"
                                    class="open-source__technology whitespace-nowrap"
                                    :class="{ '--active': selectedTechnology === item.slug }"
                                    @click="setSelectedTechnology(item.slug)">

                                    {{ item.title }}

                                </button>

                            </div>

                        </Column>

                    </Grid>

                    <Grid class="sm:mt-4 mt-8">

                        <Column>

                            <FilterButton v-if="categories.length && !show"
                                        class="md:hidden"
                                        :filters="categories"
                                        @change="setSelectedTechnology"/>

                            <span class="open-source__package-count text-base">
                                {{ isCNLocale ? `已显示${filteredPackages.length}项目` : `Showing ${filteredPackages.length} projects/plugins` }}
                            </span>

                        </Column>

                    </Grid>

                </Container>

            </div>

        <div v-if="filteredPackages.length" class="pt-16 md:pt-24 space-y-16 md:space-y-24">

            <template v-for="(openSourcePackage, idx) of filteredPackages" :key="`${openSourcePackage.title}-${idx}`">

                <OpenSourcePackage :data="openSourcePackage"/>

            </template>

        </div>

    </ThemeChanger>

    <MetaInfo v-if="cms.seo" :seo="cms.seo" :structured-data="cms.seo_structured_data"/>

</template>

<script lang="ts">

    import { computed, defineComponent, getCurrentInstance, nextTick, onBeforeMount, ref } from 'vue'
    import Container from '../components/Container.vue'
    import Column from '../components/Grid/Column.vue'
    import Grid from '../components/Grid/Grid.vue'
    import ThemeChanger from '../components/ThemeChanger.vue'
    import AdobeIcon from '../components/Logos/Adobe.vue'
    import AntDesignIcon from '../components/Logos/AntDesign.vue'
    import CtripIcon from '../components/Logos/Ctrip.vue'
    import GoogleIcon from '../components/Logos/Google.vue'
    import TencentIcon from '../components/Logos/Tencent.vue'
    import OpenSourcePackage from '../components/OpenSourcePackage.vue'
    import OpenSourceSkeleton from '../components/OpenSourceSkeleton.vue'
    import FilterButton from '../components/FilterButton.vue'
    import { useApi } from '../compositions/UseApi'
    import { currentActiveBreakPoint } from '../compositions/UseResponsiveValue'
    import { useCms } from '../compositions/UseCms'
    import { Breakpoint } from '../enums/Breakpoint'
    import { Theme } from '../enums/Theme'
    import { useTheme } from '../compositions/UseTheme'
    import { useMobileMenu } from '../compositions/menu/UseMobileMenu'
    import { stickElementOnScroll } from '../compositions/behaviors/StickElementOnScroll'
    import MetaInfo from '../components/MetaInfo.vue'
    import PageHeader from '../components/PageHeader.vue'
    import Heading from '../components/Heading.vue'

    export default defineComponent({
        name: 'OpenSource',
        components: {
            MetaInfo,
            Grid,
            Column,
            Heading,
            Container,
            OpenSourcePackage,
            FilterButton,
            OpenSourceSkeleton,
            ThemeChanger,
            PageHeader
        },
        setup() {

            useTheme().initializeCurrentThemeBasedOnRouteMeta()

            const { openSource, isCNLocale } = useCms()
            const { show } = useMobileMenu()
            const api = useApi()

            const { wrapperReference, stickyElementReference, offsetStyle, isSticky } = stickElementOnScroll({
                top: 0,
                bottom: 0
            })

            const categories = ref<any[]>([])
            const packages = ref<any[]>([])
            const featured = ref<any[]>([])
            const selectedTechnology = ref<string>('all')
            const isLoading = ref<boolean>(true)
            const refreshAOS = getCurrentInstance()?.appContext.config.globalProperties.$aos?.refresh

            onBeforeMount(async() => {

                Promise.all([ api.openSourceMetrics(isCNLocale.value ? 'cn' : 'en'), api.openSourceCategories(isCNLocale.value ? 'cn' : 'en') ]).then((responses) => {

                    if (responses[0].isOk()) {

                        for (const item of responses[0].value.data) {

                            packages.value.push(item)

                            item.featured && featured.value.push(item)

                        }

                    }

                    if (responses[1].isOk()) {

                        categories.value = [
                            {
                                title: 'All',
                                slug: 'all'
                            },
                            ...responses[1].value.data
                        ]

                    }

                    isLoading.value = false

                    nextTick(() => {

                        refreshAOS?.()

                    })

                })

            })

            return {
                Theme,
                cms: openSource,
                wrapperReference,
                stickyElementReference,
                offsetStyle,
                isSticky,
                categories,
                packages,
                featured,
                show,
                selectedTechnology,
                isLoading,
                isCNLocale,
                isSmall: computed(() => currentActiveBreakPoint.value === Breakpoint.SM),
                filteredPackages: computed(() => {

                    if (selectedTechnology.value === 'all') {

                        return packages.value

                    }

                    return packages.value.filter(pckg => pckg.category.slug === selectedTechnology.value)

                }),
                setSelectedTechnology(tech: string) {

                    selectedTechnology.value = tech

                    nextTick(() => {

                        refreshAOS?.()

                    })

                },
                logos: [
                    GoogleIcon,
                    AdobeIcon,
                    TencentIcon,
                    CtripIcon,
                    AntDesignIcon
                ]
            }

        }
    })

</script>

<style lang="scss">

    @include component(open-source) {

        @include part(sticky-wrapper) {

            /**
             * Set a hardcoded height which is the same as its inner buttons
             * To prevent the page layout from shaking when buttons become sticky
             */

            height: 72px;

        }

        @include part(sticky) {

            @apply flex w-auto;

            z-index: 50;
            transition: background-color 200ms linear;
            transition-delay: 160ms;
            will-change: background-color;

            @screen sm {

                @apply overflow-x-scroll flex-nowrap hidden;

            }

        }

        @include part(technology) {

            @apply border-pink-400 border px-6 text-base leading-none outline-none whitespace-nowrap bg-blue-100;

            height: 56px;
            transition: background-color 200ms ease-in, color 200ms ease-in;

            @screen sm {

                &:first-child {

                    margin-left: 28px;

                }

                &:last-child {

                    margin-right: 28px;

                }

            }

            &:focus {

                outline: 0;

            }

            @include option(active) {

                @apply text-pink-300 border-none outline-none bg-pure-black;

                font-weight: bold !important;

            }

            @include option(loading) {

                width: 55px;

            }

        }

    }

</style>
